import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useMemo,
  useRef,
} from "react";
import { v4 as uuid } from "uuid";
import { useAnalytics } from "contexts/AnalyticsContext";
import {
  CatalogPayload,
  CatalogTriggerName,
  SeasonalPromptSource,
} from "contexts/AnalyticsContext/AnalyticsEvents";
import { SearchResult, SearchType } from "hooks/search/types";
import { AppliedDiffEntry, FiltersDiff, RemovedDiffEntry } from "./diffFilters";

export type CatalogTrigger = CatalogTriggerName;

interface PromptSearchRunMeta {
  source: SeasonalPromptSource;
  eventId: string;
  promptId: string;
  refreshIndex: number;
  /** Normalized request the matching settle must carry. */
  targetQuery: string;
  targetSearchType: SearchType;
  targetPage: number;
}

type PendingIntent =
  | {
      trigger: "filter_applied" | "filter_removed";
      correlationId: string;
      applied: AppliedDiffEntry[];
      removed: RemovedDiffEntry[];
    }
  | {
      trigger: "query_committed";
      correlationId: string;
      previousQuery: string;
    }
  | { trigger: "type_changed"; correlationId: string; fromType: SearchType }
  | { trigger: "paginated"; correlationId: string; fromPage: number }
  | { trigger: "navigation"; correlationId: string }
  | ({ trigger: "prompt_search"; correlationId: string } & PromptSearchRunMeta);

interface CatalogAnalyticsValue {
  emit: {
    filterCommit: (diff: FiltersDiff, payloadAfter: CatalogPayload) => void;
    filterSheetOpened: (payload: CatalogPayload) => void;
    queryCommitted: (
      previousQuery: string,
      payloadAfter: CatalogPayload,
    ) => void;
    searchTypeChanged: (
      fromType: SearchType,
      payloadAfter: CatalogPayload,
    ) => void;
    paginated: (fromPage: number, payloadAfter: CatalogPayload) => void;
    resultClicked: (
      productId: string,
      position: number,
      score: number | null,
      payload: CatalogPayload,
    ) => void;
    searchCleared: (payload: CatalogPayload) => void;
  };
  registerPromptSearchIntent: (meta: PromptSearchRunMeta) => void;
  notifyFetchSettled: (
    response: SearchResult | undefined,
    payloadAfter: CatalogPayload,
  ) => void;
  clearPendingIntent: () => void;
}

const CatalogAnalyticsContext = createContext<CatalogAnalyticsValue | null>(
  null,
);

/** Deduped filter names for analytics payloads. */
const formatFilterNames = (
  entries: readonly { filter_name: string }[],
): string => Array.from(new Set(entries.map((e) => e.filter_name))).join(",");

/** Whether a settle payload carries the pending prompt's normalized request. */
const promptTargetMatchesPayload = (
  intent: PendingIntent | null,
  payload: CatalogPayload,
): boolean =>
  intent?.trigger === "prompt_search" &&
  payload.query.trim() === intent.targetQuery &&
  payload.search_type === intent.targetSearchType &&
  (payload.page ?? 1) === intent.targetPage &&
  !payload.has_filters;

export const CatalogAnalyticsProvider = ({ children }: PropsWithChildren) => {
  const { gaEvent } = useAnalytics();
  const pendingIntentRef = useRef<PendingIntent | null>(null);
  const isFirstSettleRef = useRef(true);

  const filterCommit = useCallback(
    (diff: FiltersDiff, payloadAfter: CatalogPayload) => {
      if (diff.applied.length === 0 && diff.removed.length === 0) return;

      const correlation_id = uuid();

      diff.applied.forEach((entry) => {
        gaEvent({
          type: "catalog_filter_applied",
          payload: { ...payloadAfter, correlation_id, ...entry },
        });
      });

      diff.removed.forEach((entry) => {
        gaEvent({
          type: "catalog_filter_removed",
          payload: { ...payloadAfter, correlation_id, ...entry },
        });
      });

      // Mixed apply+remove commits use filter_applied as the outcome trigger.
      pendingIntentRef.current = {
        trigger: diff.applied.length > 0 ? "filter_applied" : "filter_removed",
        correlationId: correlation_id,
        applied: diff.applied,
        removed: diff.removed,
      };
    },
    [gaEvent],
  );

  const filterSheetOpened = useCallback(
    (payload: CatalogPayload) => {
      gaEvent({
        type: "catalog_filter_sheet_opened",
        payload,
      });
    },
    [gaEvent],
  );

  const queryCommitted = useCallback(
    (previousQuery: string, payloadAfter: CatalogPayload) => {
      const correlation_id = uuid();
      gaEvent({
        type: "catalog_query_committed",
        payload: {
          ...payloadAfter,
          correlation_id,
          previous_query: previousQuery,
        },
      });
      pendingIntentRef.current = {
        trigger: "query_committed",
        correlationId: correlation_id,
        previousQuery,
      };
    },
    [gaEvent],
  );

  const searchTypeChanged = useCallback(
    (fromType: SearchType, payloadAfter: CatalogPayload) => {
      const correlation_id = uuid();
      gaEvent({
        type: "catalog_search_type_changed",
        payload: {
          ...payloadAfter,
          correlation_id,
          from_type: fromType,
        },
      });
      pendingIntentRef.current = {
        trigger: "type_changed",
        correlationId: correlation_id,
        fromType,
      };
    },
    [gaEvent],
  );

  const paginated = useCallback(
    (fromPage: number, payloadAfter: CatalogPayload) => {
      // A prompt search resetting to page 1 from a deeper page fires this same
      // page-change effect; keep the prompt intent so its settle attributes it.
      if (promptTargetMatchesPayload(pendingIntentRef.current, payloadAfter)) {
        return;
      }
      const correlation_id = uuid();
      gaEvent({
        type: "catalog_paginated",
        payload: {
          ...payloadAfter,
          correlation_id,
          from_page: fromPage,
        },
      });
      pendingIntentRef.current = {
        trigger: "paginated",
        correlationId: correlation_id,
        fromPage,
      };
    },
    [gaEvent],
  );

  const resultClicked = useCallback(
    (
      productId: string,
      position: number,
      score: number | null,
      payload: CatalogPayload,
    ) => {
      gaEvent({
        type: "catalog_result_clicked",
        payload: {
          ...payload,
          correlation_id: uuid(),
          product_id: productId,
          position,
          score,
        },
      });
    },
    [gaEvent],
  );

  const searchCleared = useCallback(
    (payload: CatalogPayload) => {
      // Clearing supersedes any pending prompt intent so a later settle
      // cannot attribute the cleared (or previous) request as a prompt run.
      pendingIntentRef.current = null;
      gaEvent({
        type: "search_cleared",
        payload: {
          ...payload,
          correlation_id: uuid(),
        },
      });
    },
    [gaEvent],
  );

  // Registers intent only; the event fires at settle via notifyFetchSettled.
  const registerPromptSearchIntent = useCallback(
    (meta: PromptSearchRunMeta) => {
      pendingIntentRef.current = {
        trigger: "prompt_search",
        correlationId: uuid(),
        ...meta,
      };
    },
    [],
  );

  const clearPendingIntent = useCallback(() => {
    pendingIntentRef.current = null;
  }, []);

  const notifyFetchSettled = useCallback(
    (response: SearchResult | undefined, payloadAfter: CatalogPayload) => {
      const isFirstSettle = isFirstSettleRef.current;
      isFirstSettleRef.current = false;

      const pendingAtStart = pendingIntentRef.current;
      let intent = pendingAtStart;

      if (intent?.trigger === "prompt_search") {
        if (!promptTargetMatchesPayload(intent, payloadAfter)) {
          // Leave the prompt intent pending for its matching settle.
          intent = null;
        } else {
          // A failed settle of this request still consumes the intent, so it
          // cannot leak onto a later request; the undefined-response return
          // below keeps the outcome events off.
          pendingIntentRef.current = null;
        }
      } else {
        pendingIntentRef.current = null;
      }

      // Deep-linked URL state synthesizes a navigation intent on first settle.
      // Skip when a prompt intent is still waiting for its matching settle.
      if (
        !intent &&
        !pendingAtStart &&
        isFirstSettle &&
        (payloadAfter.has_query || payloadAfter.has_filters)
      ) {
        intent = { trigger: "navigation", correlationId: uuid() };
      }

      if (!intent) return;

      // A failed or cancelled fetch has no outcome to report. Emitting here
      // would be indistinguishable from an instant zero-result search.
      if (response === undefined) return;

      const appliedEntries =
        intent.trigger === "filter_applied" ||
        intent.trigger === "filter_removed"
          ? intent.applied
          : [];
      const removedEntries =
        intent.trigger === "filter_applied" ||
        intent.trigger === "filter_removed"
          ? intent.removed
          : [];

      const resultsCount = response.found;

      gaEvent({
        type: "catalog_searched",
        payload: {
          ...payloadAfter,
          trigger: intent.trigger,
          correlation_id: intent.correlationId,
          applied_count: appliedEntries.length,
          removed_count: removedEntries.length,
          applied_filters: formatFilterNames(appliedEntries),
          removed_filters: formatFilterNames(removedEntries),
          results_count: resultsCount,
          search_duration_ms: response.tookMs ?? 0,
        },
      });

      if (intent.trigger === "prompt_search") {
        gaEvent({
          type: "prompt_search_run",
          payload: {
            ...payloadAfter,
            source: intent.source,
            event_id: intent.eventId,
            prompt_id: intent.promptId,
            estimated_count: null,
            results_count: resultsCount,
            refresh_index: intent.refreshIndex,
            correlation_id: intent.correlationId,
          },
        });
      }

      // Separate zero-hit event; the fetch succeeded and genuinely found none.
      if (response.found === 0) {
        gaEvent({
          type: "catalog_no_results",
          payload: {
            ...payloadAfter,
            trigger: intent.trigger,
            correlation_id: intent.correlationId,
          },
        });
      }
    },
    [gaEvent],
  );

  const value = useMemo<CatalogAnalyticsValue>(
    () => ({
      emit: {
        filterCommit,
        filterSheetOpened,
        queryCommitted,
        searchTypeChanged,
        paginated,
        resultClicked,
        searchCleared,
      },
      registerPromptSearchIntent,
      notifyFetchSettled,
      clearPendingIntent,
    }),
    [
      filterCommit,
      filterSheetOpened,
      queryCommitted,
      searchTypeChanged,
      paginated,
      resultClicked,
      searchCleared,
      registerPromptSearchIntent,
      notifyFetchSettled,
      clearPendingIntent,
    ],
  );

  return (
    <CatalogAnalyticsContext value={value}>{children}</CatalogAnalyticsContext>
  );
};

export const useCatalogAnalytics = (): CatalogAnalyticsValue => {
  const ctx = use(CatalogAnalyticsContext);
  if (!ctx) {
    throw new Error(
      "useCatalogAnalytics must be used inside <CatalogAnalyticsProvider>",
    );
  }
  return ctx;
};
